import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, enhancedFetch } from '../services/api';

const InvitationManager = ({ token, onUnauthorized, setError, setSuccess }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setErrorFlg] = useState(null);
  const [success, setSuccessFlg] = useState(null);
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [sortBy, setSortBy] = useState('created_at'); // 排序字段
  const [sortOrder, setSortOrder] = useState('desc'); // 排序顺序
  const [searchTerm, setSearchTerm] = useState(''); // 搜索关键词
  const [currentPage, setCurrentPage] = useState(1); // 当前页码
  const [itemsPerPage, setItemsPerPage] = useState(10); // 每页显示条目数

  const handleError = (error) => {
    setError(error);
    setErrorFlg(error);
  };

  const handleSuccess = (message) => {
    setSuccess(message);
    setSuccessFlg(message);
  };

  // 获取邀请码列表
  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    handleError(null);
    
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/invitations/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }, onUnauthorized);
      
      if (!response.ok) {
        throw new Error('获取邀请码失败');
      }
      
      const data = await response.json();
      setInvitations(data);
      setCurrentPage(1); // 重置到第一页
    } catch (err) {
      handleError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  // 生成新的邀请码
  const generateInvitation = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    handleError(null);
    handleSuccess(null);
    
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/invitations/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ expires_in_hours: parseInt(expiresInHours) })
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '生成邀请码失败');
      }
      
      const data = await response.json();
      handleSuccess('邀请码生成成功');
      
      // 重新获取邀请码列表
      fetchInvitations();
    } catch (err) {
      handleError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 删除邀请码
  const deleteInvitation = async (code) => {
    if (!window.confirm('确定要删除这个邀请码吗?')) {
      return;
    }
    
    setLoading(true);
    handleError(null);
    handleSuccess(null);
    
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/invitations/${code}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }, onUnauthorized);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '删除邀请码失败');
      }
      
      handleSuccess('邀请码删除成功');
      
      // 重新获取邀请码列表
      fetchInvitations();
    } catch (err) {
      handleError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // 检查是否过期
  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  // 获取状态文本
  const getStatusText = (invitation) => {
    if (invitation.is_used) return '已使用';
    if (isExpired(invitation.expires_at)) return '已过期';
    return '有效';
  };

  // 获取状态类名
  const getStatusClass = (invitation) => {
    if (invitation.is_used) return 'status-used';
    if (isExpired(invitation.expires_at)) return 'status-expired';
    return 'status-active';
  };

  // 复制邀请码到剪贴板
  const copyToClipboard = async (code, e) => {
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(code);
      handleSuccess('邀请码已复制到剪贴板');
    } catch (err) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      handleSuccess('邀请码已复制到剪贴板');
    }
  };

  // 过滤邀请码
  const filteredInvitations = invitations.filter(invitation => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      invitation.code.toLowerCase().includes(term) ||
      (invitation.used_by && invitation.used_by.toString().includes(term)) ||
      getStatusText(invitation).includes(term)
    );
  });

  // 排序邀请码
  const sortedInvitations = [...filteredInvitations].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    // 特殊处理日期字段
    if (sortBy === 'created_at' || sortBy === 'expires_at') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // 计算分页数据
  const totalPages = Math.ceil(sortedInvitations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvitations = sortedInvitations.slice(startIndex, startIndex + itemsPerPage);

  // 处理页面切换
  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // 处理每页条目数变化
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // 重置到第一页
  };

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // 显示成功消息后自动清除
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        handleSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // 显示错误消息后自动清除
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        handleError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="invitation-manager">
      <div className="card">
        <div className="card-header">
          <h2>邀请码管理</h2>
          <p>生成和管理新用户邀请码</p>
        </div>
        
        <div className="invitation-generator">
          <h3>生成新邀请码</h3>
          <form onSubmit={generateInvitation} className="form-inline">
            <div className="form-group">
              <label htmlFor="expiresInHours">过期时间 (小时):</label>
              <input
                type="number"
                id="expiresInHours"
                value={expiresInHours}
                onChange={(e) => setExpiresInHours(e.target.value)}
                min="1"
                max="8760"
                required
              />
            </div>
            <button type="submit" disabled={loading} className="primary-button">
              {loading ? '生成中...' : '生成邀请码'}
            </button>
          </form>
        </div>
        
        <div className="invitation-list">
          <div className="table-header">
            <h3>现有邀请码</h3>
            <div className="table-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="搜索邀请码、用户ID或状态..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // 重置到第一页
                  }}
                  className="search-input"
                />
              </div>
              <span className="result-count">
                {filteredInvitations.length} / {invitations.length} 个邀请码
              </span>
            </div>
          </div>
          
          {loading && <p>加载中...</p>}
          
          {invitations.length === 0 && !loading ? (
            <div className="empty-state">
              <p>暂无邀请码。</p>
              <button onClick={fetchInvitations} className="secondary-button" disabled={loading}>
                {loading ? '刷新中...' : '刷新'}
              </button>
            </div>
          ) : filteredInvitations.length === 0 && !loading ? (
            <div className="empty-state">
              <p>没有找到匹配的邀请码。</p>
              <button onClick={() => setSearchTerm('')} className="secondary-button">
                清除搜索
              </button>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="invitation-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => {
                        setSortBy('code');
                        setSortOrder(sortBy === 'code' && sortOrder === 'asc' ? 'desc' : 'asc');
                      }}>
                        邀请码
                        {sortBy === 'code' && (
                          <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="sortable" onClick={() => {
                        setSortBy('created_at');
                        setSortOrder(sortBy === 'created_at' && sortOrder === 'asc' ? 'desc' : 'asc');
                      }}>
                        创建时间
                        {sortBy === 'created_at' && (
                          <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="sortable" onClick={() => {
                        setSortBy('expires_at');
                        setSortOrder(sortBy === 'expires_at' && sortOrder === 'asc' ? 'desc' : 'asc');
                      }}>
                        过期时间
                        {sortBy === 'expires_at' && (
                          <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="sortable" onClick={() => {
                        setSortBy('is_used');
                        setSortOrder(sortBy === 'is_used' && sortOrder === 'asc' ? 'desc' : 'asc');
                      }}>
                        状态
                        {sortBy === 'is_used' && (
                          <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th>使用者</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInvitations.map((invitation) => (
                      <tr key={invitation.id}>
                        <td className="code-cell" data-label="邀请码">
                          <div className="code-content">
                            <span className="code-text">{invitation.code}</span>
                            <button 
                              className="copy-button"
                              onClick={(e) => copyToClipboard(invitation.code, e)}
                              title="复制邀请码"
                            >
                              📋
                            </button>
                          </div>
                        </td>
                        <td data-label="创建时间">{formatDate(invitation.created_at)}</td>
                        <td data-label="过期时间">{formatDate(invitation.expires_at)}</td>
                        <td data-label="状态">
                          <span className={`status-badge ${getStatusClass(invitation)}`}>
                            {getStatusText(invitation)}
                          </span>
                        </td>
                        <td data-label="使用者">{invitation.used_by ? `用户 ID: ${invitation.used_by}` : '-'}</td>
                        <td data-label="操作">
                          {!invitation.is_used && !isExpired(invitation.expires_at) && (
                            <button 
                              onClick={() => deleteInvitation(invitation.code)}
                              className="danger-button small"
                              disabled={loading}
                            >
                              删除
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        
        {/* 分页控件 */}
        {totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-info">
              <span>每页显示: </span>
              <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            
            <div className="pagination-controls">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                上一页
              </button>
              
              {[...Array(totalPages).keys()].map(number => {
                // 当总页数超过3页时，只显示3页：第一页、当前页、最后一页
                if (totalPages > 3) {
                  // 始终显示第一页
                  if (number === 0) {
                    return (
                      <button
                        key={number + 1}
                        onClick={() => handlePageChange(number + 1)}
                        className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                      >
                        {number + 1}
                      </button>
                    );
                  }
                  
                  // 当前页左边显示省略号
                  if (number === currentPage - 2 && currentPage > 2) {
                    return (
                      <span key={`ellipsis-${number}`} className="pagination-ellipsis">
                        ...
                      </span>
                    );
                  }
                  
                  // 显示当前页
                  if (number === currentPage - 1) {
                    return (
                      <button
                        key={number + 1}
                        onClick={() => handlePageChange(number + 1)}
                        className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                      >
                        {number + 1}
                      </button>
                    );
                  }
                  
                  // 当前页右边显示省略号
                  if (number === currentPage && currentPage < totalPages - 1) {
                    return (
                      <span key={`ellipsis-${number}`} className="pagination-ellipsis">
                        ...
                      </span>
                    );
                  }
                  
                  // 始终显示最后一页
                  if (number === totalPages - 1) {
                    return (
                      <button
                        key={number + 1}
                        onClick={() => handlePageChange(number + 1)}
                        className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                      >
                        {number + 1}
                      </button>
                    );
                  }
                  
                  return null;
                } else {
                  // 总页数不超过3页时，显示所有页码
                  return (
                    <button
                      key={number + 1}
                      onClick={() => handlePageChange(number + 1)}
                      className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                    >
                      {number + 1}
                    </button>
                  );
                }
              })}
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationManager;